import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import PushPanelShell from './PushPanelShell';
import InsightsChartCard, {
  INSIGHTS_CHART_TOOLTIP,
} from '../insights/InsightsChartCard';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import {
  fetchPushAnalyticsOverview,
  type PushAnalyticsOverview,
} from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { CHART_COLORS, PIE_PALETTE } from '../operations/operationsUi';

const NEUTRAL = ['#64748b', '#94a3b8', '#cbd5e1', '#475569', '#334155', '#0f172a', '#78716c', '#a8a29e'];

function formatDay(iso: string) {
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function platformLabel(p: string) {
  if (p === 'android') return 'Android';
  if (p === 'ios') return 'iOS';
  if (p === 'web') return 'Web';
  return 'Unknown';
}

const severityClass: Record<string, string> = {
  good: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
  warn: 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40',
  info: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
};

const PushAnalyticsPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PushAnalyticsOverview | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchPushAnalyticsOverview({ days })
      .then((res) => setData(res.data))
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to load analytics'));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [days]);

  const summary = data?.summary;
  const outcomePie = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'Delivered', value: Number(summary.delivered || summary.sent || 0) },
      { name: 'Failed', value: Number(summary.failed || 0) },
      { name: 'Queued', value: Number(summary.queued || 0) },
      { name: 'Skipped', value: Number(summary.skipped || 0) },
    ].filter((x) => x.value > 0);
  }, [summary]);

  const typeBar = useMemo(
    () =>
      (data?.byType || []).map((t) => ({
        name: t.label,
        delivered: t.delivered,
        failed: t.failed,
        successPct: t.successPct,
      })),
    [data?.byType]
  );

  const platformBar = useMemo(
    () =>
      (data?.byPlatform || []).map((p) => ({
        name: platformLabel(p.platform),
        sent: p.sent,
        failed: p.failed,
        read: p.read,
      })),
    [data?.byPlatform]
  );

  const recipientPie = useMemo(
    () =>
      (data?.byRecipientType || []).map((r) => ({
        name: r.recipientType,
        value: r.total,
      })),
    [data?.byRecipientType]
  );

  const cards = [
    { label: 'Attempts', value: summary?.total, hint: 'All FCM rows in period' },
    { label: 'Delivered', value: summary?.delivered ?? summary?.sent, hint: 'Successful FCM sends' },
    { label: 'Opened / read', value: summary?.read, hint: 'User opened the notification' },
    { label: 'Failed', value: summary?.failed, hint: 'FCM rejected or errored' },
    { label: 'Delivery rate', value: summary?.deliveryRatePct, suffix: '%', hint: 'Delivered ÷ attempts' },
    { label: 'Open rate', value: summary?.readRatePct, suffix: '%', hint: 'Read ÷ delivered' },
  ];

  return (
    <PushPanelShell
      title="Push analytics"
      description="Delivery health, opens, campaign-type performance, platforms, and errors — token-level FCM outcomes."
      actions={
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={0}>All time</option>
        </select>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
            <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
              {loading || card.value == null
                ? '—'
                : `${Number(card.value).toLocaleString()}${card.suffix || ''}`}
            </p>
            <p className="mt-0.5 text-[10px] text-gray-400">{card.hint}</p>
          </div>
        ))}
      </div>

      {(data?.insights || []).length > 0 ? (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data!.insights.map((insight) => (
            <div
              key={insight.title}
              className={`rounded-xl border p-3 ${severityClass[insight.severity] || severityClass.info}`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                {insight.severity === 'good' ? 'Insight' : insight.severity === 'warn' ? 'Watch' : 'Note'}
              </p>
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{insight.title}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{insight.detail}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mb-4 grid gap-4 xl:grid-cols-7">
        <InsightsChartCard title="Daily sends, failures & opens" className="xl:col-span-4" heightClass="h-72">
          {loading ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</p>
          ) : !(data?.dailyTrend || []).length ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">No trend data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data!.dailyTrend}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDay}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={INSIGHTS_CHART_TOOLTIP}
                  labelFormatter={(v) => formatDay(String(v))}
                />
                <Area type="monotone" dataKey="sent" name="Sent" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.15} />
                <Area type="monotone" dataKey="failed" name="Failed" stroke="#64748b" fill="#94a3b8" fillOpacity={0.12} />
                <Area type="monotone" dataKey="read" name="Opened" stroke="#334155" fill="#64748b" fillOpacity={0.1} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <InsightsChartCard title="Outcome mix" className="xl:col-span-3" heightClass="h-72">
          {loading ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</p>
          ) : outcomePie.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">No outcomes yet</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={outcomePie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {outcomePie.map((_, i) => (
                    <Cell key={i} fill={NEUTRAL[i % NEUTRAL.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>
      </div>

      <div className="mb-4 grid gap-4 xl:grid-cols-2">
        <InsightsChartCard title="Performance by campaign type" heightClass="h-72">
          {loading ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</p>
          ) : typeBar.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">No type totals yet</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={typeBar}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Bar dataKey="delivered" name="Delivered" fill="#475569" radius={[4, 4, 0, 0]} />
                <Bar dataKey="failed" name="Failed" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <InsightsChartCard title="By platform" heightClass="h-72">
          {loading ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</p>
          ) : platformBar.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">No platform data</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={platformBar}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                <Bar dataKey="sent" name="Sent" fill="#334155" radius={[4, 4, 0, 0]} />
                <Bar dataKey="read" name="Opened" fill="#64748b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="failed" name="Failed" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>
      </div>

      <div className="mb-4 grid gap-4 xl:grid-cols-5">
        <InsightsChartCard title="Audience mix" className="xl:col-span-2" heightClass="h-64">
          {loading ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">Loading…</p>
          ) : recipientPie.length === 0 ? (
            <p className="flex h-full items-center justify-center text-sm text-gray-400">No audience mix</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={recipientPie} dataKey="value" nameKey="name" outerRadius={85}>
                  {recipientPie.map((_, i) => (
                    <Cell key={i} fill={(PIE_PALETTE || NEUTRAL)[i % (PIE_PALETTE || NEUTRAL).length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </InsightsChartCard>

        <div className="xl:col-span-3 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Top FCM errors</p>
            <p className="text-xs text-gray-500">Most common failure codes in this period</p>
          </div>
          <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Error</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={2} className="px-4 py-8 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : !(data?.topErrors || []).length ? (
                <tr>
                  <td colSpan={2} className="px-4 py-8 text-center text-gray-400">
                    No failures recorded — good.
                  </td>
                </tr>
              ) : (
                data!.topErrors.map((err) => (
                  <tr key={err.code}>
                    <td className="px-4 py-2">
                      <p className="font-medium text-gray-900 dark:text-white">{err.label}</p>
                      <p className="text-xs text-gray-500">{err.hint}</p>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-800 dark:text-gray-200">
                      {err.count.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Top campaigns by deliveries</p>
            <p className="text-xs text-gray-500">All-time cumulative FCM totals on each campaign</p>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Campaign</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Delivered</th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Failed</th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Success</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : !(data?.topCampaigns || []).length ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No campaigns with deliveries yet.
                </td>
              </tr>
            ) : (
              data!.topCampaigns.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                  <td className="px-4 py-2">
                    <Link
                      to={`${PUSH_HUB_BASE_PATH}/campaigns/${c.id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {c.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{c.notificationType}</td>
                  <td className="px-4 py-2 text-right">{c.delivered.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{c.failed.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{c.successPct}%</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{c.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(data?.byType || []).length > 0 ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Type breakdown</p>
          </div>
          <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Campaigns</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Delivered</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Failed</th>
                <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500">Success %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {data!.byType.map((t) => (
                <tr key={t.notificationType}>
                  <td className="px-4 py-2 font-medium">{t.label}</td>
                  <td className="px-4 py-2 text-right">{t.campaignCount.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{t.delivered.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{t.failed.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">{t.successPct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushAnalyticsPanel;
